import type { BuildingTranslations } from "../../types/buildings";
import { buildingCore } from "./building-sections/core";
import { buildingDetails } from "./building-sections/details";
import { technicalSystems } from "./building-sections/technical-systems";
import { finishes } from "./building-sections/finishes";
import { structuralMaterials } from "./building-sections/structural-materials";
import { fireSafety } from "./building-sections/fire-safety";
import { roomsSection } from "./building-sections/rooms";
import { chat } from "./building-sections/chat";
import { sidebar } from "./building-sections/sidebar";
import { buildingList } from "./building-sections/list";
import { buildingDataWidget } from "./building-sections/building-data-widget";
import { ifcViewer } from "./building-sections/ifc-viewer";

// Combine all building section translations
export const buildings: BuildingTranslations = {
  // Core building properties
  ...buildingCore,

  // Additional details and form
  ...buildingDetails,

  // Technical systems
  technicalSystems,

  // Finishes
  finishes,

  // Structural materials
  structuralMaterials,

  // Fire safety
  fireSafety,

  // Rooms section
  roomsSection,

  // List translations
  list: buildingList,

  // Chat functionality
  chat,

  // Sidebar navigation
  sidebar,

  // Global building/project scope and its read-only context label
  scope: {
    label: "Scope",
    contextLabel: "Building and project context",
    selectProject: "Select a project",
    switchProject: "Switch project",
    searchPlaceholder: "Search projects...",
    noProjects: "No projects found.",
    loadFailed: "Couldn't load the projects for this building.",
    chooseProjectFolders: "Choose a project to view its folders.",
  },

  // Projects-view dashboard (single-project perspective)
  projectsDashboard: {
    title: "Project dashboard",
    myTasks: {
      title: "My tasks",
      dueWithin: "Due within",
      window: {
        oneWeek: "1 week",
        twoWeeks: "2 weeks",
        oneMonth: "1 month",
      },
      empty: "No tasks due in this window",
      viewAll: "View all my tasks",
      documentsCount: "Linked documents: {count}",
      commentsCount: "Comments: {count}",
    },
    uploadedToday: {
      title: "Uploaded today",
      viewAll: "View all",
      empty: "Nothing uploaded yet today.",
    },
    recentChanges: {
      title: "What's new",
      timeframeLabel: "Last",
      timeframe: {
        day: "24 h",
        threeDays: "72 h",
        week: "7 days",
        month: "30 days",
      },
      summary: {
        newIssues: "New issues",
        completedIssues: "Issues completed",
        updatedIssues: "Issues updated",
        comments: "Comments",
        newDocuments: "New documents",
        newVersions: "New versions",
      },
      nothingChanged: "Nothing changed in this timeframe.",
      moreItems: "+{count} more",
      documentsPartialAccess:
        "Document changes aren't shown, because you don't have access to all of this project's documents.",
      atLeastHint: "At least this many — there was too much activity to count it all.",
      loadFailed: "Couldn't load the changes.",
      forYou: {
        title: "For you",
        mentioned: "{actor} mentioned you on {subject}",
        assigned: "{actor} assigned you to {subject}",
        discipline: "New in {discipline}: {subject}",
        someone: "Someone",
        more: "+{count} more",
      },
    },
  },

  // Building Data sidebar widget
  buildingDataWidget,

  // 3D model (IFC) viewer surface (WHA-3689)
  ifcViewer,

  // Navigation actions
  viewDashboard: "View dashboard",
  viewBuilding: "View building",
  mapError: "Error loading map.",
  geocodeError: "Failed to determine address from location.",
  projectionsPage: {
    title: "Budget projections",
    chartTitle: "Monthly projections",
  },
  budgetPage: {
    loading: "Loading budget data...",
    loadError: "Failed to load budget data",
    saveError: "Failed to save budget data",
  },
  myBuildings: "My Buildings",
  myArchivedBuildings: "My Archived Buildings",
  archive: "Archive",
  restore: "Restore",
  archived: "Building archived",
  movedToArchive: "The building has been moved to the archive.",
  archiveError: "Failed to archive building. Please try again.",
  restored: "Building restored",
  restoredFromArchive: "The building has been restored from the archive.",
  restoreError: "Failed to restore building. Please try again.",
  renameError: "Failed to rename building. Please try again.",
  archiveTitle: "Archive Building",
  restoreTitle: "Restore Building",
  archiveConfirm:
    "Are you sure you want to archive this building? You can restore it later from the archive.",
  restoreConfirm:
    "Are you sure you want to restore this building? It will be moved back to active buildings.",
  selectCertificateType: "Please select a certificate type",
  buildingInformation: "Building Information",
  addRenovationDate: "Add renovation date...",
  selectBuildingType: "Select building type",
  grossArea: "Gross Area (m\u00B2)",
  heatedArea: "Heated Area (m\u00B2)",
  leasableArea: "Leasable Area (m\u00B2)",
  residentialArea: "Residential Area (m\u00B2)",
  commercialArea: "Commercial Area (m\u00B2)",
  areaValidationError: "Area validation mismatch",
  floorAreaRatio: "Floor Area Ratio",
  buildingRightTotal: "Building right total (gross area) (m\u00B2)",
  buildingRightLeft: "Building right left (m\u00B2)",
  bicycleParkingSpots: "Bicycle parking spots",
  evChargingSpots: "EV charging spots",
  selectOrgBeforeCreating: "Please select an organisation before creating a building.",
  creatingBuilding: "Creating Building...",
  nameMinLength: "Building name must be at least 2 characters.",
  addressMinLength: "Address must be at least 5 characters.",
};
